import { useEffect, useMemo, useState, useCallback } from "react";
import toast from "react-hot-toast";

import { Modal } from "shared/ui";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { TextArea } from "shared/ui/ae-user-input";
import { combineDateTimeWithZone, DayJs } from "shared/lib/helpers/date";
import { clsxMerge, getFullEntityName } from "shared/lib/helpers";
import CalendarForm, { CalendarDateI } from "shared/ui/calendar-form";
import { DATE_FORMAT_ONLY_TIME } from "shared/lib/constants/time";

import { MeetingTakerDropdown } from "@/components/modules/pipeline/account-details/shared/meeting-taker-dropdown";
import { MeetingTakerI } from "@/interfaces/accounts";
import { useAccountsContext } from "@/hooks/use-accounts-context";
import { glencocoClientAPI } from "@/api/glencoco";
import { dd } from "@/helpers/datadog";
import { ERROR_CATEGORIES } from "@/constants/errors";
import { dispatchCustomEvent } from "@/helpers/events";
import { CUSTOM_EVENTS } from "@/constants/custom-events";

import {
  SchedulingLinkModal,
  SCHEDULING_LINK_MODAL_ID,
} from "./scheduling-link-modal";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

const SimpleTextArea = TextArea();

export const X_BOOK_MEETING_MODAL_ID = "x-book-meeting-modal";

const NOTES_INPUT_ID = `${X_BOOK_MEETING_MODAL_ID}-notes-input`;

/**
 * Check if meeting taker should use scheduling link flow
 * (has both scheduling link and connected calendar)
 */
const shouldUseSchedulingLink = (meetingTaker: MeetingTakerI | undefined) => {
  return Boolean(
    meetingTaker?.calendar_link && meetingTaker?.is_calendar_connected
  );
};

interface ModalContentPropsI {
  onSuccess?: () => void;
  onOpenSchedulingLink: (meetingTaker: MeetingTakerI) => void;
}

const ModalContent = ({
  onSuccess,
  onOpenSchedulingLink,
}: ModalContentPropsI) => {
  const { modalAccount } = useAccountsContext();

  const [meetingTime, setMeetingTime] = useState<string>();
  const [meetingTaker, setMeetingTaker] = useState<MeetingTakerI>();
  const [notes, setNotes] = useState<string>();

  const meetingTakerId = meetingTaker?.id;

  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    setIsDisabled(!meetingTime || !meetingTakerId);
  }, [meetingTime, meetingTakerId]);

  const [isLoading, setIsLoading] = useState(false);

  const handleOnChange = ({ date, time, timezone }: CalendarDateI) => {
    if (!time) {
      return;
    }

    setMeetingTime?.(
      combineDateTimeWithZone(date, time, timezone).toISOString()
    );
  };

  const handleOpenSchedulingLink = useCallback(() => {
    if (meetingTaker) {
      onOpenSchedulingLink(meetingTaker);
    }
  }, [meetingTaker, onOpenSchedulingLink]);

  const handleOnChangeMeetingTaker = useCallback(
    (selectedMeetingTaker: MeetingTakerI) => {
      setMeetingTaker(selectedMeetingTaker);
    },
    [onOpenSchedulingLink]
  );

  const handleOnNotesChange = (notesValue: string) => {
    setNotes(notesValue);
  };

  const handleBookMeeting = async () => {
    setIsLoading(true);

    const campaignId = modalAccount?.campaignId as string;
    const accountId = modalAccount?.accountId as string;
    const contactId = modalAccount?.contact?.id as string;

    const API = glencocoClientAPI();

    const Resp = await API.bookMeeting(campaignId, accountId, contactId, {
      account_executive_id: meetingTakerId as string,
      meeting_time: meetingTime as string,
      notes,
    }).catch((e) => e);

    if (Resp.status === 200) {
      dispatchCustomEvent({
        eventType: CUSTOM_EVENTS.ACCOUNT.RELOAD,
      });
      dispatchCustomEvent({
        eventType: CUSTOM_EVENTS.ACCOUNT.HISTORY.RELOAD,
      });

      onSuccess?.();
    } else {
      toast.error("Failed to book a meeting");

      dd.rum.error(
        `${ERROR_CATEGORIES.BOOK_MEETING} - failed to book a meeting`,
        {
          data: Resp,
        }
      );
    }

    setIsLoading(false);
  };

  const initialTime = useMemo(() => {
    if (!modalAccount?.followUpTime) {
      return;
    }

    const time = DayJs(modalAccount?.followUpTime);

    return time.format(DATE_FORMAT_ONLY_TIME);
  }, [modalAccount?.followUpTime]);

  // Determine if we should show the scheduling link option
  const canUseSchedulingLink = shouldUseSchedulingLink(meetingTaker);

  return (
    <div>
      <div className="mb-6 flex flex-col">
        <label className="mb-3 typography-body-4-bold">
          Who are you setting the meeting for?
        </label>
        <MeetingTakerDropdown
          className="w-full"
          textFieldClassName="w-full"
          onChange={handleOnChangeMeetingTaker}
          defaultValue={modalAccount?.accountExecutive?.id}
        />

        {meetingTaker?.calendar_link && (
          <div className="mt-3 typography-body-4">
            <b>{getFullEntityName(meetingTaker)}'s</b>{" "}
            <a
              target="_blank"
              href={meetingTaker.calendar_link}
              rel="noreferrer"
              className="ae-link"
            >
              meeting link
            </a>
          </div>
        )}

        {canUseSchedulingLink && (
          <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="mb-2 text-green-800 typography-body-4">
              {getFullEntityName(meetingTaker)} has a connected calendar. You
              can book directly through their scheduling link and we'll
              automatically sync the meeting.
            </p>

            <button
              type="button"
              onClick={handleOpenSchedulingLink}
              className="text-green-700 typography-body-4-bold hover:underline"
            >
              Book via scheduling link (recommended)
            </button>
          </div>
        )}
      </div>

      <CalendarForm
        title="Set meeting date"
        bookingDeadlineInMonths={false}
        isNotes={false}
        onDateChange={handleOnChange}
        initialDate={modalAccount?.followUpTime}
        initialTime={initialTime}
      />

      <div>
        <SimpleTextArea
          id={NOTES_INPUT_ID}
          name="notes"
          label="Notes"
          placeholder="Type your notes here"
          inputClassName="max-h-[50vh] min-h-[130px] bg-white border border-black/20"
          labelClassName="typography-body-4 text-black"
          inputProps={{
            onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
              handleOnNotesChange(ev.target.value);
            },
          }}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="typography-body-4-bold">
          This will be moved to your booked meetings list.
        </span>
        <button
          className={clsxMerge(
            "btn-ae-default btn-lg min-w-[170px] typography-header-8-semibold",
            {
              loading: isLoading,
              disabled: isDisabled || isLoading,
            }
          )}
          disabled={isDisabled || isLoading}
          onClick={handleBookMeeting}
        >
          Save meeting
        </button>
      </div>
    </div>
  );
};

export const GlenXBookMeetingModal = ({
  modalId = X_BOOK_MEETING_MODAL_ID,
}: {
  modalId?: string;
} & PropsWithClassNameI) => {
  const [schedulingLinkMeetingTaker, setSchedulingLinkMeetingTaker] =
    useState<MeetingTakerI | null>(null);

  const handleOpenSchedulingLink = useCallback(
    (meetingTaker: MeetingTakerI) => {
      // Store the meeting taker for the scheduling link modal
      setSchedulingLinkMeetingTaker(meetingTaker);

      // Close the main modal first
      modalHelpers.close(X_BOOK_MEETING_MODAL_ID);

      // Open scheduling link modal after a brief delay
      setTimeout(() => {
        modalHelpers.open(SCHEDULING_LINK_MODAL_ID);
      }, 100);
    },
    []
  );

  const handleSchedulingLinkSuccess = useCallback(() => {
    setSchedulingLinkMeetingTaker(null);
    modalHelpers.close(SCHEDULING_LINK_MODAL_ID);
    toast.success("Meeting booked successfully!");
  }, []);

  const handleSchedulingLinkClose = useCallback(() => {
    setSchedulingLinkMeetingTaker(null);
  }, []);

  return (
    <>
      <Modal
        id={modalId}
        showCloseButton
        modalBoxClassName="overflow-y-auto"
        title="Book meeting"
        titleClassName="typography-header-6 mb-10"
        unmountOnClose
      >
        {({ handleClose }) => (
          <ModalContent
            onSuccess={handleClose}
            onOpenSchedulingLink={handleOpenSchedulingLink}
          />
        )}
      </Modal>

      {/* Scheduling Link Modal - rendered outside main modal */}
      {schedulingLinkMeetingTaker && (
        <SchedulingLinkModal
          meetingTaker={schedulingLinkMeetingTaker}
          onSuccess={handleSchedulingLinkSuccess}
          onClose={handleSchedulingLinkClose}
        />
      )}
    </>
  );
};
